import { useState, useMemo } from "react";
import { useLocation as useAppLocation } from "@/contexts/LocationContext";
import StoreCard from "@/components/stores/StoreCard";
import { Search, MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const Stores = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showOpenOnly, setShowOpenOnly] = useState(false);
  const { nearbyStores, selectedLocation, isLoadingStores } = useAppLocation();

  const filteredStores = useMemo(() => {
    let result = [...nearbyStores];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.description.toLowerCase().includes(query) ||
          s.address.toLowerCase().includes(query),
      );
    }

    if (showOpenOnly) {
      result = result.filter((s) => s.isOpen);
    }

    return result;
  }, [searchQuery, showOpenOnly, nearbyStores]);

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-gradient-hero text-primary-foreground py-10">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5" />
            <span className="text-sm font-medium text-primary-foreground/70">Near {selectedLocation.label}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">Local Stores</h1>
          <p className="text-primary-foreground/70 text-sm">
            {isLoadingStores ? "Finding stores..." : `${nearbyStores.length} stores available in your area`}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-card border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            variant={showOpenOnly ? "default" : "outline"}
            onClick={() => setShowOpenOnly(!showOpenOnly)}
            className="h-11"
          >
            {showOpenOnly ? "Open Now ✓" : "Show Open Only"}
          </Button>
        </div>

        {isLoadingStores ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-40 rounded-2xl" />
            ))}
          </div>
        ) : filteredStores.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredStores.map((store, i) => (
              <StoreCard key={store.id} store={store} index={i} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="font-heading font-semibold text-lg">No stores found</p>
            <p className="text-sm text-muted-foreground mt-1">Try adjusting your search</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Stores;
